import * as yup from 'yup';

// An empty field is no id (null)
const optionalId = (label) =>
	yup
		.number()
		.transform((value, originalValue) => (originalValue === '' ? null : value))
		.typeError(`${label} must be a number`)
		.integer(`${label} must be a whole number`)
		.positive(`${label} must be a positive number`)
		.nullable();

/** The feature flag whose broker groups pick sales team and authorised rep from client config. */
const CLIENT_CONFIG_INSIGHT_SELECTION_FLAG = 'clientConfigInsightSelection';

/** The broker groups clientConfigInsightSelection targets, from listAllFeatureFlags. */
export const getClientConfigBrokerGroupIds = (flags) =>
	flags.find(({ feature }) => feature === CLIENT_CONFIG_INSIGHT_SELECTION_FLAG)?.brokerGroups ??
	[];

/**
 * Whether a new user needs their own sales team and authorised rep ids: their primary
 * broker group isn't one of clientConfigInsightSelection's (whose users get them from
 * client config), and they're not a Folio admin. Even then, each is only required while
 * its Insight list has something to pick (isInsightIdRequired). Kept the same as the
 * backend's createUser.
 */
export const needsInsightIds = ({ brokerGroupId, isFolioAdmin }, clientConfigBrokerGroupIds) =>
	Boolean(brokerGroupId) && !isFolioAdmin && !clientConfigBrokerGroupIds.includes(brokerGroupId);

/** The Insight ids a new user needs (needsInsightIds), with the message when one is empty. */
export const INSIGHT_REQUIRED_MESSAGES = {
	salesTeamId: 'Sales team id is required for this broker group',
	authorisedRepId: 'Authorised rep id is required for this broker group',
};

/**
 * Whether one of a new user's INSIGHT_REQUIRED_MESSAGES fields is required, given that
 * they needInsightIds: unless its ledger's Insight list has nothing to pick (no values, only
 * inactive ones, or the ledger's Insight doesn't offer it). While it loads, or if Insight
 * couldn't be read for another reason, it's required. Kept the same as the backend's
 * getInsightFieldsWithoutValues.
 *
 * @param {object} [list] the field's Insight list once it has loaded or failed, as
 *   toInsightList gives it: { options, isError, isNotAvailable }
 */
export const isInsightIdRequired = (list) =>
	!list ||
	(!list.isNotAvailable &&
		(list.isError || list.options.some(({ isInactive }) => !isInactive)));

/**
 * Whether an email can be given Folio admin access: Folio's own (@folio.insure), or a Folio
 * support login in a brokerage's tenant, whose address starts with "folio" (folio@…,
 * folio.admin@…, folioadmin@…). Kept the same as the backend's services/validation/user.js.
 */
export const isFolioAdminEmail = (email) => {
	const [localPart, domain] = String(email ?? '').toLowerCase().split('@');
	return domain === 'folio.insure' || Boolean(localPart?.startsWith('folio'));
};

/**
 * A user as an admin edits them, kept the same as the backend's
 * services/validation/user.js (the admin API also checks the broker groups exist, that the
 * email isn't another user's, and that an admin isn't changing their own Folio admin
 * access). Text is trimmed, and the email lowercased, when cast.
 *
 * - name: required
 * - email: a valid email address, required, and not another user's (from `users`; the API
 *   checks again when saving, against users added since the list loaded)
 * - phoneNumber: free text, as in the CRM's user profile
 * - role: optional, one of the CRM's roles (UserRoleSelect)
 * - brokerGroupId: the primary broker group, required
 * - brokerGroupArray: the other broker groups, which must be on the primary's ledger
 * - imageUrl: the photo's URL (a data URL for a new photo until Save uploads it)
 * - Insight ids: a positive whole number or empty (null). Whether a new user's sales team
 *   and authorised rep are required depends on their Insight lists, so it's checked where
 *   those are loaded (the drawer's InsightIdField, the import's checkRows), not here.
 * - permissions: true / false. Turning Folio admin on needs a Folio email
 *   (isFolioAdminEmail); a user who's already one (wasFolioAdmin) isn't checked, so existing
 *   admins keep their access.
 *
 * @param {Map} brokerGroupsById broker group id → broker group, from listAllBrokerGroups
 * @param {object[]} users every user, from listAllUsers
 * @param {object} [options]
 * @param {string} [options.userId] the user being edited, whose own email is fine
 * @param {boolean} [options.wasFolioAdmin] whether the user being edited is already a Folio admin
 */
export const getUserValidationSchema = (
	brokerGroupsById,
	users,
	{ userId, wasFolioAdmin = false } = {},
) =>
	yup.object({
		name: yup.string().trim().required('Name cannot be empty'),
		email: yup
			.string()
			.trim()
			.lowercase()
			.required('Email address is required.')
			.email('Email address must be a valid email')
			.test('not-taken', function (email) {
				const other = users.find(
					(user) => user.id !== userId && user.email?.trim().toLowerCase() === email,
				);
				return (
					!other ||
					this.createError({
						message: `${email} is already the email of ${other.name ?? 'another user'}`,
					})
				);
			}),
		phoneNumber: yup.string().trim(),
		role: yup.string().trim(),
		imageUrl: yup.string(),
		brokerGroupId: yup.string().required('Primary broker group is required'),
		brokerGroupArray: yup
			.array()
			.of(yup.string())
			.test('same-ledger', function (ids) {
				const ledger = brokerGroupsById.get(this.parent.brokerGroupId)?.ledger;
				const offLedger = (ids ?? [])
					.map((id) => brokerGroupsById.get(id))
					.filter((brokerGroup) => ledger && brokerGroup && brokerGroup.ledger !== ledger);
				return (
					!offLedger.length ||
					this.createError({
						message: `Broker groups must all be on the primary's ledger (${ledger}): ${offLedger
							.map(({ name, ledger: otherLedger }) => `${name} is on ${otherLedger}`)
							.join(', ')}`,
					})
				);
			}),
		salesTeamId: optionalId('Sales team id'),
		serviceTeamId: optionalId('Service team id'),
		accountTypeId: optionalId('Account type id'),
		authorisedRepId: optionalId('Authorised rep id'),
		canViewGroup: yup.boolean(),
		principleUser: yup.boolean(),
		isNetworkAdmin: yup.boolean(),
		isFolioAdmin: yup
			.boolean()
			.test(
				'folio-email',
				'Folio admin access needs a Folio email: @folio.insure, or an address starting with "folio"',
				function (isFolioAdmin) {
					return !isFolioAdmin || wasFolioAdmin || isFolioAdminEmail(this.parent.email);
				},
			),
		complianceManager: yup.boolean(),
	});
